import React from 'react';
import { Loader2, UserPlus, Users, Trash2, X, Mail, ChevronRight } from 'lucide-react';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { displayedName } from '../../data/publisherConfigs';
import { ApiError } from '../../lib/apiClient';
import Banner from '../../components/self-service/shared/Banner';
import { rankOf, ROLE_LABEL } from '../../components/self-service/shared/roleRank';
import {
  stationTeamService,
  type InviteResult,
  type InviteResultSummary,
  type TeamPageData,
} from '../../services/stationTeamService';

const PublisherTeamPage: React.FC = () => {
  const { slug, config, user } = useSingleStationContext();
  const callerRank = rankOf(user.rawRole);

  const [data, setData] = React.useState<TeamPageData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [emailsText, setEmailsText] = React.useState('');
  const [inviting, setInviting] = React.useState(false);
  const [inviteSummary, setInviteSummary] =
    React.useState<InviteResultSummary | null>(null);
  const [legendOpen, setLegendOpen] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await stationTeamService.listTeam(slug);
      setData(resp);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to load team.',
      );
    } finally {
      setLoading(false);
    }
  }, [slug]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSubmitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const emails = emailsText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (emails.length === 0) return;
    setInviting(true);
    setInviteSummary(null);
    try {
      const summary = await stationTeamService.inviteReps(slug, emails);
      setInviteSummary(summary);
      // Clear successful entries; leave rejected ones so the user can fix.
      const rejected = summary.results
        .filter(
          (r) =>
            r.status === 'rejected_other_org' ||
            r.status === 'rejected_invalid_email',
        )
        .map((r) => r.email);
      setEmailsText(rejected.join('\n'));
      await refresh();
    } catch (err) {
      setInviteSummary(null);
      setActionError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to send invites.',
      );
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string, email: string) => {
    if (!window.confirm(`Revoke pending invite for ${email}?`)) return;
    try {
      await stationTeamService.revokeInvite(slug, inviteId);
      await refresh();
    } catch (e) {
      setActionError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to revoke invite.',
      );
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    try {
      await stationTeamService.resendInvite(slug, inviteId);
      await refresh();
    } catch (e) {
      setActionError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to resend invite.',
      );
    }
  };

  const handleRemoveRep = async (userId: string, email: string) => {
    if (!window.confirm(`Remove ${email} from ${config.name ?? displayedName(config)}?`))
      return;
    try {
      await stationTeamService.removeRep(slug, userId);
      await refresh();
    } catch (e) {
      setActionError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to remove rep.',
      );
    }
  };

  const stationName = config.name ?? displayedName(config);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold text-slate-900">Team</h1>
        <button
          type="button"
          onClick={() => {
            setInviteOpen(true);
            setInviteSummary(null);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
        >
          <UserPlus className="w-4 h-4" />
          Invite reps
        </button>
      </div>
      <p className="text-sm text-slate-600 mb-6">
        Add and remove station reps for {stationName}. Reps can submit and
        review advertiser campaigns for this station.
      </p>

      {actionError && (
        <div className="mb-4">
          <Banner
            tone="warn"
            label="Action failed"
            rightSlot={
              <button
                type="button"
                onClick={() => setActionError(null)}
                className="text-rose-600 hover:text-rose-800"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            }
          >
            {actionError}
          </Banner>
        </div>
      )}

      {error ? (
        <Banner tone="warn" label="Couldn't load team">{error}</Banner>
      ) : loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : data ? (
        <>
          <section className="bg-white border border-slate-200 rounded-xl mb-6">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-900">Active reps</h2>
              <span className="ml-auto text-xs text-slate-500 tabular-nums">
                {data.activeReps.length}
              </span>
            </div>
            <div className="px-5 py-2 border-b border-slate-100">
              <button
                type="button"
                onClick={() => setLegendOpen((v) => !v)}
                aria-expanded={legendOpen}
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
              >
                <ChevronRight
                  className={`w-3.5 h-3.5 transition-transform ${legendOpen ? 'rotate-90' : ''}`}
                />
                Role tags
              </button>
              {legendOpen && (
                <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-xs">
                  <dt>
                    <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-800">
                      Org Admin
                    </span>
                  </dt>
                  <dd className="text-slate-600 self-center">
                    Access to every station in this organization.
                  </dd>
                  <dt>
                    <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                      Station Rep
                    </span>
                  </dt>
                  <dd className="text-slate-600 self-center">
                    Access to specific stations.
                  </dd>
                </dl>
              )}
            </div>
            {data.activeReps.length === 0 ? (
              <div className="px-5 py-6 text-sm text-slate-500">
                No active reps yet.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.activeReps.map((rep) => {
                  const isSelf = rep.userId === user.id;
                  // Server filters out super_admins from the list, so this
                  // branch is defensive only — if one slips through (e.g.,
                  // metadata race), keep the row but hide Remove.
                  const isSuperAdmin = rep.role === 'super_admin';
                  // Org_admins are read-only via this surface — their access
                  // is role-based and only manageable on the legacy admin
                  // Users page. Hide Remove entirely (matches super_admin).
                  const isOrgAdmin = rep.role === 'organization_admin';
                  const removeButtonHidden = isSuperAdmin || isOrgAdmin;
                  // Rank check mirrors the server: a caller can remove peers
                  // and below, but not roles ranked higher than their own.
                  const targetRank = rankOf(rep.role);
                  const insufficientRank = targetRank > callerRank;
                  const disabledRemove = isSelf || insufficientRank;
                  const removeTitle = isSelf
                    ? "You can't remove yourself; ask a teammate"
                    : insufficientRank
                      ? `Only an ${ROLE_LABEL[rep.role] ?? rep.role} (or higher) can remove this user`
                      : `Remove ${rep.email}`;
                  return (
                    <li
                      key={rep.userId}
                      className="px-5 py-3 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {rep.email}
                          {isSelf && (
                            <span className="ml-1.5 text-xs text-slate-500">
                              · You
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          <RoleBadge role={rep.role} />
                          {rep.otherStationCount > 0 && (
                            <span className="ml-2">
                              +{rep.otherStationCount} other station
                              {rep.otherStationCount === 1 ? '' : 's'}
                            </span>
                          )}
                          {rep.lastSignInAt && (
                            <span className="ml-2">
                              Last sign-in{' '}
                              {new Date(rep.lastSignInAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      {!removeButtonHidden && (
                        <button
                          type="button"
                          title={removeTitle}
                          disabled={disabledRemove}
                          onClick={() => handleRemoveRep(rep.userId, rep.email)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-3 h-3" />
                          Remove
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-xl">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-900">
                Pending invites
              </h2>
              <span className="ml-auto text-xs text-slate-500 tabular-nums">
                {data.pendingInvites.length}
              </span>
            </div>
            {data.pendingInvites.length === 0 ? (
              <div className="px-5 py-6 text-sm text-slate-500">
                No pending invites.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.pendingInvites.map((invite) => (
                  <li
                    key={invite.id}
                    className="px-5 py-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {invite.email}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleResendInvite(invite.id)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
                      >
                        Resend
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleRevokeInvite(invite.id, invite.email)
                        }
                        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50"
                      >
                        <Trash2 className="w-3 h-3" />
                        Revoke
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}

      {inviteOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => {
              setInviteOpen(false);
              setInviteSummary(null);
              setEmailsText('');
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-5"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Invite station reps
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Inviting to{' '}
                  <span className="font-medium text-slate-900">
                    {stationName}
                  </span>
                  . One email per line, up to 50.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setInviteOpen(false);
                  setInviteSummary(null);
                  setEmailsText('');
                }}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitInvite}>
              <textarea
                rows={6}
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                placeholder="rep1@example.com&#10;rep2@example.com"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setInviteOpen(false);
                    setInviteSummary(null);
                    setEmailsText('');
                  }}
                  disabled={inviting}
                  className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting || !emailsText.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inviting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Send invites
                </button>
              </div>
            </form>

            {inviteSummary && (
              <div className="mt-4 space-y-2">
                <InviteResultBreakdown
                  summary={inviteSummary}
                  stationName={stationName}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// CTV-583: two user-facing tags on this surface — Org Admin (for
// organization_admin) and Station Rep (for sales_rep OR manager). The
// underlying-role granularity is intentionally hidden here because it's noise
// for the publisher-facing view; the Remove-button rank tooltip still reads
// ROLE_LABEL from roleRank.ts, where the granularity is load-bearing.
const ROLE_TONE: Record<string, string> = {
  organization_admin: 'bg-violet-100 text-violet-800',
};

function roleTag(role: string): 'Org Admin' | 'Station Rep' {
  return role === 'organization_admin' ? 'Org Admin' : 'Station Rep';
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span
      className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${ROLE_TONE[role] ?? 'bg-slate-100 text-slate-700'}`}
    >
      {roleTag(role)}
    </span>
  );
}

// Per-recipient breakdown of an invite batch. Each outcome gets its own
// labeled banner so the user can see exactly which email landed in which
// category — counts alone (e.g., "1 already had access") were confusing
// because the email wasn't named.
function InviteResultBreakdown({
  summary,
  stationName,
}: {
  summary: InviteResultSummary;
  stationName: string;
}) {
  const groupBy = (statuses: InviteResult['status'][]) =>
    summary.results.filter((r) => statuses.includes(r.status));

  const sent = groupBy(['invite_sent']);
  const added = groupBy(['access_added']);
  const skipped = groupBy(['already_has_access']);
  // Split "already had access" by role so the copy can be specific —
  // org_admins/super_admins have global access that isn't actionable here;
  // sales_reps/managers are just already-fully-active on this station.
  const skippedOrgWide = skipped.filter(
    (r) => r.targetRole === 'organization_admin' || r.targetRole === 'super_admin',
  );
  const skippedStationScoped = skipped.filter(
    (r) => !(r.targetRole === 'organization_admin' || r.targetRole === 'super_admin'),
  );
  const rejectedOtherOrg = groupBy(['rejected_other_org']);
  const rejectedInvalid = groupBy(['rejected_invalid_email']);

  return (
    <>
      {sent.length > 0 && (
        <Banner tone="success" label={`Invites sent (${sent.length})`}>
          <p className="mb-1">
            These people will get an email invitation to join {stationName}.
            They'll appear under <strong>Pending invites</strong> until they
            accept.
          </p>
          <ul className="list-disc list-inside">
            {sent.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {added.length > 0 && (
        <Banner tone="success" label={`Added to ${stationName} (${added.length})`}>
          <p className="mb-1">
            These people already had a CTV Buyer account in your organization.
            We've added <strong>{stationName}</strong> to their station list — no
            invitation email was needed. They got a heads-up email with a link
            to the admin dashboard.
          </p>
          <ul className="list-disc list-inside">
            {added.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {skippedOrgWide.length > 0 && (
        <Banner
          tone="info"
          label={`Already had access — org-wide role (${skippedOrgWide.length})`}
        >
          <p className="mb-1">
            These people are organization admins (or super admins) and already
            have access to every station in this organization. The Team tab
            can't add or remove their access — that's managed on the legacy
            admin Users page.
          </p>
          <ul className="list-disc list-inside">
            {skippedOrgWide.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {skippedStationScoped.length > 0 && (
        <Banner
          tone="info"
          label={`Already had access (${skippedStationScoped.length})`}
        >
          <p className="mb-1">
            These people are already station reps on{' '}
            <strong>{stationName}</strong> with active access. No change was
            made.
          </p>
          <ul className="list-disc list-inside">
            {skippedStationScoped.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {rejectedOtherOrg.length > 0 && (
        <Banner
          tone="warn"
          label={`Couldn't invite — different publisher (${rejectedOtherOrg.length})`}
        >
          <p className="mb-1">
            An account with this email already exists at another publisher.
            Contact CTV Buyer ops to merge accounts, or use a different email.
          </p>
          <ul className="list-disc list-inside">
            {rejectedOtherOrg.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {rejectedInvalid.length > 0 && (
        <Banner
          tone="warn"
          label={`Couldn't invite — invalid email (${rejectedInvalid.length})`}
        >
          <p className="mb-1">Check for typos and try again.</p>
          <ul className="list-disc list-inside">
            {rejectedInvalid.map((r) => (
              <li key={r.email} className="font-medium">
                {r.email}
                {r.error ? ` — ${r.error}` : ''}
              </li>
            ))}
          </ul>
        </Banner>
      )}
    </>
  );
}

export default PublisherTeamPage;
